import { routeTo } from "../../../Components/CustomHooks/routeChange";
import { BaseAction } from "../../../core/actions/BaseAction";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { STORAGE_KEYS } from "../../../Lib/Constants/storageKeys";
import { calculatePrice } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { AncillaryStatusEnum, TripTypeEnum, TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { isNolAncillary } from "../../../Lib/Utils/nolCard";
import { IBookedOffer, IBookingTypeTrip, IReservation } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IAlreadyAddonsForPassenger, IAlreadyAddonsLegPassengerAddons } from "../../../Orchestrator/Types/Interface/IAddOns";
import { calculateAlreadyPaidAddonsAction } from "../../LogicActions/Addons/CalculateAlreadyPaidAddons";
import { probeRefundOffersAction } from "../../LogicActions/Addons/ProbeRefundOffersAction";
import { getBookingAction } from "../../LogicActions/Booking/GetBookingAction";
import { prepareBookingSummaryAction } from "../../LogicActions/Checkout/Summary/PrepareBookingSummaryAction";
import { resetManageSeatingState } from "./ResetManageSeatingStateAction";
import { deleteAncillaryFromOffer } from "../../../app/api/deleteAncillaryFromOffer";
import { clearBookingFlowAtoms } from "../../../Orchestrator/Recoils/clearBookingFlowAtoms";

// Use shared reset to keep behavior consistent everywhere
const resetSeatingAtoms = () => resetManageSeatingState();

export class ManageSeatingRedirectClickAction extends BaseAction<IDefaultActionResult, {
    bookingId: string,
    tripData: any
}> {
    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        }
        try {
            // Wipe stale flow state from any prior aborted checkout before
            // seeding this flow's writes (see clearBookingFlowAtoms doc).
            clearBookingFlowAtoms();
            // Reset all seating-related atoms before starting new manage seating flow
            resetSeatingAtoms();

            const { bookingId, tripData } = this.params!;
            updateRecoilState("displayPassengerDataAtom", tripData.passengers);
            updateRecoilState("selectedTripsDataAtom", tripData);
            updateRecoilState("isManageSeatingAtom", true);
            updateRecoilState("isHyperFastStateAtom", true);
            updateRecoilState("isQuickExchangeStateAtom", true);
            // Wipe any pending refund IDs from a prior session so a fresh
            // manage-seating doesn't drag in deselections that have already
            // been paid out (or abandoned). Belt-and-suspenders alongside
            // clearBookingFlowAtoms (which also targets this atom).
            updateRecoilState("pendingRefundFulfillmentIdsAtom", null);
            // Persist flag so a page refresh on /checkout can rehydrate the
            // flow-mode atoms. Mirrors the isUpgradeToPremiumFlow pattern used
            // by UpgradeToPremiumAction. Cleared by PaymentGatewayCheckoutAction
            // on payment success and by app/bookings + app/search on navigation.
            //
            // Also clear stale flow flags left by abandoned upgrade/CDT attempts.
            // isChangeDateFlow in particular poisons getAddonsFromBookingAction:
            // it forces a PREBOOKED-only offer filter, which wipes every
            // FULFILLED offer in manage-seating and leaves mealPreferencesResponseAtom
            // empty → the Extras card never renders. Same failure mode is called
            // out in PrepareBookingSummaryAction.ts:930 for booking-details; the
            // cleanup just wasn't wired into the manage-seating entry.
            if (typeof window !== "undefined") {
                localStorage.removeItem(STORAGE_KEYS.CHANGE_DATE_FLOW);
                localStorage.removeItem("isChangeDateFlowForToast");
                localStorage.removeItem("isUpgradeToPremiumFlow");
                localStorage.setItem("isManageSeatingFlow", "true");
            }

            // Pass filtering parameters to GetBookingAction
            getBookingAction.setParams({
                bookingId,
                direction: tripData.direction,
                displayPassengerData: tripData.passengers,
                selectedTripsData: tripData
            });
            await getBookingAction.execute();

            if (getBookingAction.status.executionStatus === ExecutionStatus.SUCCESS) {
                let response: any = getBookingAction.result;

                // Strip PREBOOKED ancillaries from the booking on entry to
                // manage-seating. These are unpaid reservations so deletion is
                // money-safe (no refund needed). Rationale: the prior behavior
                // re-POSTed ancillaries on every confirm, which caused
                // cross-leg contamination and stale-state bugs (see also the
                // scope fix in ConfirmRefundedAddonsAction). By clearing the
                // slate here, each leg's addon picks are independent and the
                // user ends up with exactly the set they confirmed. FULFILLED
                // (paid) and REFUNDED ancillaries are intentionally left
                // untouched — deleting FULFILLED would require a refund flow.
                try {
                    // Scan the current booking state for PREBOOKED ancillaries.
                    const scanPrebooked = (
                        bookingData: any
                    ): { offerId: string; ancillaryId: string }[] => {
                        const targets: { offerId: string; ancillaryId: string }[] = [];
                        const offers = bookingData?.bookedOffers || [];
                        // Flatten once: the NOL product an ancillary references can
                        // live on a sibling offer, not just the offer that owns it.
                        const allProducts = offers.flatMap((o: any) => o?.products || []);
                        for (const offer of offers) {
                            for (const anc of (offer?.ancillaries || [])) {
                                if (
                                    anc?.status === AncillaryStatusEnum.PREBOOKED &&
                                    anc?.id &&
                                    offer?.offerId
                                ) {
                                    // NOL day-passes can be added post-checkout via
                                    // confirmNol with no payment step, so a legitimate
                                    // pass can sit PREBOOKED indefinitely — exclude NOL
                                    // from this purge or an active pass gets silently
                                    // deleted on manage-seating entry.
                                    if (isNolAncillary(allProducts, anc)) continue;
                                    targets.push({
                                        offerId: offer.offerId,
                                        ancillaryId: anc.id,
                                    });
                                }
                            }
                        }
                        return targets;
                    };

                    // Retry loop: delete, re-fetch, re-scan. Stop when no PREBOOKED
                    // remain or we hit the attempt cap. Covers transient backend
                    // errors and the occasional partial-success response so the
                    // user really does get a fresh slate to re-pick extras.
                    const MAX_CLEANUP_ATTEMPTS = 3;
                    let attempt = 0;
                    let remaining = scanPrebooked(response?.data?.booking);
                    const allSuccessfullyDeletedIds = new Set<string>();

                    while (remaining.length > 0 && attempt < MAX_CLEANUP_ATTEMPTS) {
                        attempt += 1;
                        console.info(
                            `[ManageSeatingRedirectClickAction] PREBOOKED cleanup attempt ${attempt}/${MAX_CLEANUP_ATTEMPTS} — deleting ${remaining.length} ancillary/ies`
                        );

                        const results = await Promise.all(
                            remaining.map((t) =>
                                deleteAncillaryFromOffer(
                                    bookingId,
                                    t.offerId,
                                    t.ancillaryId,
                                    false
                                )
                                    .then((res) => ({ ...t, res }))
                                    .catch((err) => ({
                                        ...t,
                                        res: { status: "ko", message: String(err) },
                                    }))
                            )
                        );
                        for (const r of results) {
                            if (r.res.status === "ok") {
                                allSuccessfullyDeletedIds.add(r.ancillaryId);
                            }
                        }
                        const failed = results.filter((r) => r.res.status !== "ok");
                        if (failed.length > 0) {
                            console.warn(
                                `[ManageSeatingRedirectClickAction] Attempt ${attempt}: ${failed.length}/${results.length} DELETEs failed`,
                                failed.map((f) => f.ancillaryId)
                            );
                        }

                        // Re-fetch booking so the next scan sees authoritative state.
                        await getBookingAction.execute();
                        const refreshed: any = getBookingAction.result;
                        if (refreshed?.data?.booking) {
                            response = refreshed;
                        } else {
                            // Refetch failed — patch in-memory response and stop
                            // retrying; otherwise we'd loop indefinitely on stale
                            // data (the same PREBOOKED ids keep appearing in scan).
                            console.warn(
                                "[ManageSeatingRedirectClickAction] booking re-fetch returned empty payload; patching in-memory response and aborting retry loop"
                            );
                            const inMemoryBooking = response?.data?.booking;
                            if (inMemoryBooking?.bookedOffers && allSuccessfullyDeletedIds.size > 0) {
                                inMemoryBooking.bookedOffers = inMemoryBooking.bookedOffers.map(
                                    (offer: any) => ({
                                        ...offer,
                                        ancillaries: (offer.ancillaries || []).filter(
                                            (anc: any) => !allSuccessfullyDeletedIds.has(anc?.id)
                                        ),
                                    })
                                );
                            }
                            break;
                        }
                        remaining = scanPrebooked(response?.data?.booking);
                    }

                    if (remaining.length > 0) {
                        // The SyncAlreadyAddonsAction FULFILLED-only filter (same
                        // branch) guarantees these ghosts won't render in the UI,
                        // but log loudly so ops can investigate the backend.
                        console.error(
                            `[ManageSeatingRedirectClickAction] ${remaining.length} PREBOOKED ancillary/ies could not be deleted after ${attempt} attempt(s):`,
                            remaining
                        );
                    } else if (attempt > 0) {
                        console.info(
                            `[ManageSeatingRedirectClickAction] PREBOOKED cleanup complete after ${attempt} attempt(s)`
                        );
                    }
                } catch (cleanupError) {
                    console.error(
                        "[ManageSeatingRedirectClickAction] prebooked-ancillary cleanup failed:",
                        cleanupError
                    );
                }

                if (response && response.data && response.data.booking) {
                    prepareBookingSummaryAction.setParams({ ...response.data.booking, isManageSeating: true })
                    await prepareBookingSummaryAction.execute();
                    // Speculative refund-offer probe: POST /refund-offers with
                    // every FULFILLED ancillary id so AddonsModal can show
                    // accurate per-meal refundFee + refundableAmount up-front
                    // (instead of approximating with mod-fee math). The action
                    // is non-fatal — a failure just means the modal falls back
                    // to the naive computation. Fire-and-forget so the UI
                    // doesn't stall behind a 200-400ms speculative call;
                    // AddonsModal subscribes to the cache atom and re-renders
                    // when the probe lands.
                    probeRefundOffersAction.execute();
                    const filteredBookingPassengers = response.data.booking?.passengers || [];
                    updateRecoilState("passengerStateAtom", null)
                    updateRecoilState("passengerStateAtom", filteredBookingPassengers);

                    // Store original seat selections for comparison
                    const trips = response.data.booking?.trips || [];
                    const bookedOffers = response.data.booking?.bookedOffers || [];
                    const originalSeats: { place?: string, coach?: string }[][] = [];

                    trips.forEach((trip: any, tripIndex: number) => {
                        const tripOriginalSeats: { place?: string, coach?: string }[] = [];

                        bookedOffers.forEach((offer: any) => {
                            if (offer.tripCoverage?.coveredTripId === trip.id) {
                                offer.reservations?.forEach((reservation: any) => {
                                    // Extract seats from placeAllocation.reservedPlaces
                                    const reservedPlaces = reservation.placeAllocation?.reservedPlaces || [];
                                    reservedPlaces.forEach((place: any) => {
                                        if (place.coachNumber && place.placeNumbers && place.placeNumbers.length > 0) {
                                            place.placeNumbers.forEach((placeNumber: string) => {
                                                tripOriginalSeats.push({
                                                    place: placeNumber,
                                                    coach: place.coachNumber
                                                });
                                            });
                                        }
                                    });
                                });
                            }
                        });

                        originalSeats[tripIndex] = tripOriginalSeats;
                    });

                    updateRecoilState("originalSeatsAtom", originalSeats);
                    let allBookedOfferWithoutExchange: IBookedOffer[] = response?.allBookedOfferWithoutExchange || []
                    let allBookedTripWithoutExchange: IBookingTypeTrip[] = response?.allBookedTripsWithoutExchange || []
                    if (allBookedOfferWithoutExchange.length > 0 && allBookedTripWithoutExchange.length > 0) {
                        const alreadyAddonsLegPassenger: IAlreadyAddonsLegPassengerAddons = {}
                        // Flatten once: post-exchange the NOL product an ancillary
                        // references can live on a sibling offer, not just the offer
                        // that owns it.
                        const allProducts = allBookedOfferWithoutExchange.flatMap((o) => o?.products || []);
                        allBookedOfferWithoutExchange.forEach((bookedOffer: IBookedOffer) => {
                            let bookTrip = allBookedTripWithoutExchange.find(d => d.id === bookedOffer.tripCoverage.coveredTripId)
                            // Only FULFILLED ancillaries pre-select in manage-seating:
                            // PREBOOKED were just deleted above (on entry), and REFUNDED
                            // are already removed by refund. This is belt-and-suspenders
                            // defense so a partial-delete failure can't leak PREBOOKED
                            // ancillaries into the pre-selection set (which would paint
                            // ghost items in the Extras card that no longer exist
                            // server-side).
                            const allAncillaryForTrip: IReservation[] = bookedOffer.ancillaries.filter(d => {
                                if (d.tripCoverage.coveredTripId !== bookTrip?.id || d.status !== AncillaryStatusEnum.FULFILLED) return false;
                                // NOL passes ride the booking as ancillaries but belong to the
                                // End-to-end Journey flow, never the Extras baseline. Without this,
                                // the manage-seating footer treats the Day Pass as a removable Extra
                                // and quotes a phantom refund fee. Mirrors SyncAlreadyAddonsAction.
                                if (isNolAncillary(allProducts, d)) return false;
                                return true;
                            })
                            const legWiseAlreadyAddons: IAlreadyAddonsForPassenger[] = []
                            for (const ancillaries of allAncillaryForTrip) {
                                const passengerDetails = filteredBookingPassengers.find((d: any) => d.id === ancillaries.passengerIds[0])
                                const productId = ancillaries.products[0].productId
                                const productDetails = bookedOffer.products.find(d => d.id === productId)
                                const bookTripLeg = bookTrip?.legs.find(d => d.id === ancillaries.tripCoverage.coveredLegIds[0])
                                const passenger: IAlreadyAddonsForPassenger = {
                                    category: ancillaries.type,
                                    addonName: productDetails?.description || "",
                                    passengerId: passengerDetails?.id || "",
                                    passengerName: `${passengerDetails?.detail?.firstName || ""} ${passengerDetails?.detail?.lastName || ""}`,
                                    passengerRef: passengerDetails?.externalRef || "",
                                    type: productDetails?.code || "",
                                    price: calculatePrice(ancillaries?.price) || 0,
                                    stopCode: `${bookTripLeg?.timedLeg.start.stopPlaceRef.stopPlaceRef}-${bookTripLeg?.timedLeg.end.stopPlaceRef.stopPlaceRef}`,
                                    legId: bookTripLeg?.id || "",
                                    ancillaryId: ancillaries.id
                                }
                                legWiseAlreadyAddons.push(passenger)
                            }
                            let direction = bookTrip?.direction || ""
                            direction = direction === TripTypeEnum.IN_BOUND ? TrainTypeEnum.inbound : TrainTypeEnum.outbound
                            alreadyAddonsLegPassenger[direction] = [
                                ...(alreadyAddonsLegPassenger[direction] || []),
                                ...legWiseAlreadyAddons,
                            ]
                        })
                        updateRecoilState("alreadyAddonsLegPassengerAtom", alreadyAddonsLegPassenger)
                        updateRecoilState("manageSeatingBaselineAddonsLegPassengerAtom", alreadyAddonsLegPassenger)
                        const totalAlreadyPaidPrice = [...alreadyAddonsLegPassenger?.outbound || [], ...alreadyAddonsLegPassenger?.inbound || []]
                        const legTotals = Object.values(
                            totalAlreadyPaidPrice.reduce((acc, item: IAlreadyAddonsForPassenger) => {
                                const key = item.legId ?? "UNKNOWN";
                                acc[key] = acc[key] || { legId: key, totalPrice: 0 };
                                acc[key].totalPrice += item.price || 0;
                                return acc;
                            }, {} as Record<string, { legId: string; totalPrice: number }>)
                        );
                        updateRecoilState("alreadyAddonsLegPassengerTotalAtom", legTotals)
                    }
                }
                updateRecoilState("paymentStateAtom", null);
                updateRecoilState("paymentStateAtom", {
                    // Left unset so <Payment> can preselect the customer's default saved
                    // card — it only preselects while no method is set (Payment.tsx).
                    method: null,
                    total: 0,
                });

                actionResult.status = "success"
            } else {
                console.error("GetBookingAction failed:", getBookingAction.status.error);
                actionResult.message = "Failed to fetch booking data"
            }
        } catch (error) {
            console.error("Error during manage seating redirect click action:", error);
            actionResult.message = "Something went wrong"
        } finally {
            return actionResult
        }
    }
}

export const manageSeatingRedirectClickAction = new ManageSeatingRedirectClickAction();
